import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

type Variant = 'primary' | 'success' | 'ghost' | 'outline' | 'subtle';
type Size = 'sm' | 'md';

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-primary text-white hover:bg-primary-hover shadow-sm',
  success: 'bg-success-strong text-white hover:bg-success',
  outline: 'border border-hairline bg-transparent text-ink hover:bg-hover',
  ghost: 'bg-transparent text-ink-muted hover:text-ink-strong hover:bg-hover',
  subtle: 'bg-hover text-ink hover:bg-hover-fuerte border border-hairline',
};

const SIZES: Record<Size, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5',
  md: 'h-9 px-4 text-sm gap-2',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  icon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'subtle', size = 'sm', icon, className, children, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      className={cn(
        'inline-flex items-center justify-center rounded-lg font-medium',
        // Se hunde 4% al apretar: es el acuse de recibo mas barato que hay. La transicion
        // nombra las propiedades exactas — `transition-all` animaria tambien el layout.
        'transition-[color,background-color,border-color,scale] duration-150 active:scale-[0.96]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-ring',
        'disabled:cursor-not-allowed disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...props}
    >
      {icon}
      {children}
    </button>
  );
});
